import { Box, useTheme } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useModal from '../../../hooks/useModal/useModal';
import useRolePermissions from '../../../hooks/useRolePermissions/useRolePermissions';
import usePortalConfig from '../../../hooks/usePortalConfig/usePortalConfig';
import { Admin, AllowedAdminOperations } from '../../../models/AdminOnboardingModels';
import MenuItemWrapper from '../../../components/Dropdown/DropdownStyle';
import { VerticalDotsOutlineIcon } from '../../../components/Icons/VerticalDotsIcon';
import MenuWrapper from '../../../components/Menu/MenuStyle';

interface AdminRowOptionsProps {
  admin: Admin;
  deleteAdmin: (adminId: string) => void;
}
function AdminRowOptions({ admin, deleteAdmin }: AdminRowOptionsProps) {
  const [t] = useTranslation();
  const theme = useTheme();
  const modal = useModal();
  const { isUserAllowed } = useRolePermissions();
  const { routePermissions } = usePortalConfig();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  /**
   * Handle button click
   * @param event
   */
  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Handle menu close
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Displays the confirmation modal
   */
  const showConfirmationModal = () => {
    modal.showModal({
      modalTitle: t('pages.adminOnboarding.deleteAdmin'),
      modalDescription: (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <span>{t('pages.adminOnboarding.deleteAdminMessage1')}</span>
          <span>{t('pages.adminOnboarding.deleteAdminMessage2')}</span>
        </Box>
      ),
      confirmationModal: true,
      confirmationButtonLabel: t('common.delete'),
      onAccept: (setOpen) => {
        setOpen(false);
        deleteAdmin(admin.id);
      },
    });
  };

  /**
   * View as Admin option
   */
  const viewAsAdmin = () => {
    handleClose();
    window.open(`${window.location.origin}/impersonate/${admin.id}`, '_blank', 'noopener');
  };

  /**
   * Validation to know if we can render the options menu
   */
  const canRenderOptions = () =>
    isUserAllowed(routePermissions.adminsCreate) &&
    (admin.allowedOperations.includes(AllowedAdminOperations.CAN_DELETE) ||
      admin.allowedOperations.includes(AllowedAdminOperations.CAN_IMPERSONATE));

  return (
    <>
      {canRenderOptions() ? (
        <Box
          id={`box-${admin.id}`}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: '0.375rem',
            width: '2.25rem',
            height: '2.25rem',
            flexShrink: 0,
            ':hover': { backgroundColor: theme.palette.monochrome.monoChrome11, cursor: 'pointer' },
          }}
          onClick={handleClick}
        >
          <VerticalDotsOutlineIcon stroke="#E60000" />
        </Box>
      ) : null}
      <Box>
        <MenuWrapper
          id={`menu-${admin.id}`}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {admin.allowedOperations.includes(AllowedAdminOperations.CAN_IMPERSONATE) ? (
            <MenuItemWrapper onClick={viewAsAdmin}>{t('pages.adminOnboarding.viewAsAdmin')}</MenuItemWrapper>
          ) : null}
          {admin.allowedOperations.includes(AllowedAdminOperations.CAN_DELETE) ? (
            <MenuItemWrapper onClick={showConfirmationModal} sx={{ color: theme.palette.primary.main }}>
              {t('pages.adminOnboarding.removeAdmin')}
            </MenuItemWrapper>
          ) : null}
        </MenuWrapper>
      </Box>
    </>
  );
}

export default AdminRowOptions;
